import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import Solutions from './pages/Solutions';
import Team from './pages/Team';
import Resources from './pages/Resources';
import Contact from './pages/Contact';
import Professor from './pages/Professor';
import IndustryProjects from './pages/IndustryProjects';
import Science from './pages/Science';
import MemberProfile from './pages/MemberProfile';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
});

const ScrollHandler: React.FC = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    } else {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [pathname, hash]);

  return null;
};

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="min-h-screen pb-12 transition-theme"
    >
      {children}
    </motion.div>
  );
};

const App: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('theme-dark');
    } else {
      root.classList.remove('theme-dark');
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Router>
        <ScrollHandler />
        <div className="relative min-h-screen transition-theme">
          <Navbar />

          <main className="relative z-10">
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
                <Route path="/solutions" element={<PageWrapper><Solutions /></PageWrapper>} />
                <Route path="/team" element={<PageWrapper><Team /></PageWrapper>} />
                <Route path="/professor" element={<PageWrapper><Professor /></PageWrapper>} />
                <Route path="/resources" element={<PageWrapper><Resources /></PageWrapper>} />
                <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} />
                <Route path="/industry-projects" element={<PageWrapper><IndustryProjects /></PageWrapper>} />
                <Route path="/science" element={<PageWrapper><Science /></PageWrapper>} />
                {/* Private member profiles — intentionally not linked anywhere; reach by typing the URL. Password checked server-side in /api/verify-member. */}
                <Route path="/members/:slug" element={<PageWrapper><MemberProfile /></PageWrapper>} />
              </Routes>
            </AnimatePresence>
          </main>

          <Footer />
        </div>
      </Router>
    </ThemeContext.Provider>
  );
};

export default App;